import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Compass, ShoppingBag, LayoutDashboard, User, BarChart3, TrendingUp, Users } from 'lucide-react';
import { apiService } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import {
  COMMUNITY_SEEN_EVENT,
  communityBadgeCount,
  markCommunitySeen,
} from '../../utils/communitySeen';

// Community sits second rather than last on purpose: it's the one tab whose
// whole value depends on people remembering it exists, and a forum nobody
// opens is a forum that dies. The other three are destinations people go
// looking for anyway.
const CREATOR_TABS = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/community', label: 'Community', icon: Users, badgeKey: 'community' },
  { to: '/analytics', label: 'Analytics', icon: BarChart3 },
  { to: '/transactions', label: 'Transactions', icon: TrendingUp },
  { to: '/profile', label: 'Profile', icon: User },
];

const BUYER_TABS = [
  { to: '/explore', label: 'Explore', icon: Compass },
  { to: '/buyer/dashboard', label: 'Purchases', icon: ShoppingBag },
];

// Same shape and cap (99+, never an ever-widening pill) as the admin
// sidebar's NavBadge — a creator and an admin should read "there are open
// things waiting" the same way wherever they see it. aria-hidden because the
// count is decorative next to the tab's own accessible name; a screen reader
// announcing "Community, 12" on every tab sweep would be noise, not signal.
const TabBadge = ({ count }) => {
  if (!count) return null;
  return (
    <span
      aria-hidden="true"
      className="absolute -top-1 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center leading-none ring-2 ring-white"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
};

// Native-style bottom nav shown only inside the installed Android/iOS app
// shell (see usePlatform) — never rendered in a regular browser tab. Tab set
// switches between Buyer and Creator flows based on the current route.
export default function AppTabBar() {
  const { pathname } = useLocation();
  const isBuyer = pathname.startsWith('/buyer');
  const inCommunity = pathname === '/community' || pathname.startsWith('/community/');
  const tabs = isBuyer ? BUYER_TABS : CREATOR_TABS;
  const { isAuthenticated, authChecked, user } = useAuth();
  const uid = user?.uid;

  // How many community posts are waiting for an answer right now — the
  // same needs_answer flag PostCard and the "Needs an answer" filter use,
  // counted once server-side (see /api/community/meta) so this can never
  // disagree with what a creator finds after tapping in. Fetched here
  // rather than inside the Community page itself because the whole point
  // of a tab badge is to be visible from OUTSIDE that page — someone on
  // their Dashboard should see that people need help without opening the
  // forum first.
  //
  // What it counts is what's arrived since this creator last opened the forum,
  // not the forum's running total — see utils/communitySeen. The raw total is
  // ~40 on any given day and never drops to zero, so as a badge it said "43"
  // permanently and stopped meaning anything.
  const [communityBadge, setCommunityBadge] = useState(0);
  useEffect(() => {
    // /api/community/meta requires a creator session and 401s for a buyer
    // or a not-yet-authenticated visitor — most commonly someone who just
    // opened the installed app shell and hasn't logged in yet. That used to
    // fire the request anyway and swallow the resulting 401, which is real
    // load for a call bound to fail, and the failure was indistinguishable
    // from a genuine bug in the admin error log. Wait for a confirmed
    // session instead: authChecked gates against the moment before
    // Firebase's initial auth state has even resolved, so a creator who IS
    // logged in doesn't lose their badge to a race on first paint.
    if (isBuyer || !authChecked || !isAuthenticated) { setCommunityBadge(0); return undefined; }
    let alive = true;
    const load = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const meta = await apiService.getCommunityMeta();
        const total = meta?.needs_answer_count || 0;
        // Standing in the forum IS reading it. Mark the count seen here as
        // well as on the page itself, so the badge is gone by the time the
        // feed has finished painting rather than a beat afterwards.
        if (inCommunity) {
          markCommunitySeen(uid, total);
          if (alive) setCommunityBadge(0);
          return;
        }
        if (alive) setCommunityBadge(communityBadgeCount(uid, total));
      } catch {
        if (alive) setCommunityBadge(0);
      }
    };
    // No timer. The installed app shell is the worst case for one — it is open
    // for a whole session by definition, so a 2-minute poll here ran all day
    // for a number nobody was watching. Fetched on mount and whenever the app
    // is brought back to the foreground, which is the only moment it can be
    // read. See NotificationBell for the fuller reasoning.
    load();
    document.addEventListener('visibilitychange', load);
    // The tab bar doesn't remount when you navigate to /community inside the
    // app shell, so the page's own "seen" write has to reach it as an event
    // or the badge would sit there stale until the next foreground.
    const onSeen = () => { if (alive) setCommunityBadge(0); };
    window.addEventListener(COMMUNITY_SEEN_EVENT, onSeen);
    return () => {
      alive = false;
      document.removeEventListener('visibilitychange', load);
      window.removeEventListener(COMMUNITY_SEEN_EVENT, onSeen);
    };
  }, [isBuyer, authChecked, isAuthenticated, inCommunity, uid]);

  return (
    <nav className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 pb-safe-b">
      <div className="grid h-16" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>
        {tabs.map(({ to, label, icon: Icon, badgeKey }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-1 text-[11px] px-1 ${
                isActive ? 'text-mint-main font-medium' : 'text-gray-500'
              }`
            }
          >
            <span className="relative inline-flex">
              <Icon size={20} />
              {badgeKey === 'community' && <TabBadge count={communityBadge} />}
            </span>
            <span className="truncate max-w-full">{label}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
